import { createContext, useContext, useEffect, useState } from 'react';

export interface Preferences {
  typeface: 'system_sans' | 'serif' | 'dyslexia_friendly';
  textScale: number;
  lineSpacing: number;
  contentWidth: 'narrow' | 'default' | 'wide';
  theme: 'light' | 'dark' | 'high_contrast';
  reducedMotion: boolean;
}

const DEFAULTS: Preferences = {
  typeface: 'system_sans', textScale: 100, lineSpacing: 1.6,
  contentWidth: 'default', theme: 'light', reducedMotion: false,
};

const KEY = 'map.preferences';
const Ctx = createContext<{ prefs: Preferences; set: (p: Partial<Preferences>) => void }>({
  prefs: DEFAULTS, set: () => {},
});
export const usePreferences = () => useContext(Ctx);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<Preferences>(() => {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') }; }
    catch { return DEFAULTS; }
  });

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--text-scale', String(prefs.textScale));
    root.style.setProperty('--line-spacing', String(prefs.lineSpacing));
    root.dataset.typeface = prefs.typeface;
    root.dataset.width = prefs.contentWidth;
    root.dataset.theme = prefs.theme;
    // The OS preference is a floor: if the system asks for reduced motion we
    // honour it even when the stored preference has not been set (FR-070).
    const system = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.dataset.reducedMotion = String(prefs.reducedMotion || system);
    localStorage.setItem(KEY, JSON.stringify(prefs));
  }, [prefs]);

  return (
    <Ctx.Provider value={{ prefs, set: (p) => setPrefs((cur) => ({ ...cur, ...p })) }}>
      {children}
    </Ctx.Provider>
  );
}

export function PreferencesPanel() {
  const { prefs, set } = usePreferences();
  return (
    <fieldset>
      <legend>Reading preferences</legend>
      <p className="muted">These are saved on this device and apply to every session.</p>

      <label htmlFor="pref-typeface">Typeface</label>
      <select id="pref-typeface" value={prefs.typeface}
              onChange={(e) => set({ typeface: e.target.value as Preferences['typeface'] })}>
        <option value="system_sans">Sans serif (system)</option>
        <option value="serif">Serif</option>
        <option value="dyslexia_friendly">Dyslexia-friendly</option>
      </select>

      <label htmlFor="pref-scale" style={{ marginTop: '1rem' }}>
        Text size: {prefs.textScale}%
      </label>
      <input id="pref-scale" type="range" min={100} max={200} step={10} value={prefs.textScale}
             onChange={(e) => set({ textScale: Number(e.target.value) })}
             style={{ width: '100%', maxWidth: '22rem' }} />

      <label htmlFor="pref-spacing" style={{ marginTop: '1rem' }}>
        Line spacing: {prefs.lineSpacing.toFixed(1)}
      </label>
      <input id="pref-spacing" type="range" min={1.2} max={2.4} step={0.1} value={prefs.lineSpacing}
             onChange={(e) => set({ lineSpacing: Number(e.target.value) })}
             style={{ width: '100%', maxWidth: '22rem' }} />

      <label htmlFor="pref-width" style={{ marginTop: '1rem' }}>Content width</label>
      <select id="pref-width" value={prefs.contentWidth}
              onChange={(e) => set({ contentWidth: e.target.value as Preferences['contentWidth'] })}>
        <option value="narrow">Narrow</option><option value="default">Default</option><option value="wide">Wide</option>
      </select>

      <label htmlFor="pref-theme" style={{ marginTop: '1rem' }}>Theme</label>
      <select id="pref-theme" value={prefs.theme}
              onChange={(e) => set({ theme: e.target.value as Preferences['theme'] })}>
        <option value="light">Light</option><option value="dark">Dark</option>
        <option value="high_contrast">High contrast</option>
      </select>

      <label style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input type="checkbox" checked={prefs.reducedMotion}
               onChange={(e) => set({ reducedMotion: e.target.checked })} />
        Reduce motion
      </label>
    </fieldset>
  );
}
